/**
 * Wires up the textarea + outer container refs so the textarea grows with
 * its content and the surrounding "original comment" panel matches its
 * height. Recomputation is debounced 100ms to coalesce keystrokes.
 *
 * A `ResizeObserver` on the write-back column also runs recompute whenever
 * that column's height changes (for example when writeback QC issues render
 * below the textarea), so the original-comment panel background stays aligned.
 *
 * @module busniessOffice/components/singleComment/hooks/useAutoGrowTextarea
 */

import { useCallback, useEffect, useRef } from "react";

/**
 * @returns
 * - `textareaRef` to attach to the `<textarea>`
 * - `writeBackContainerRef` to attach to the wrapping write-back panel
 *   (its `scrollHeight` becomes the matching column's height)
 * - `commentsContainerRef` to attach to the original-comment panel
 *   whose `style.height` we adjust
 * - `recompute()` to trigger a height recalculation (call from `onChange`).
 */
export const useAutoGrowTextarea = () => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const writeBackContainerRef = useRef<HTMLDivElement>(null);
  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const recompute = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      const textarea = textareaRef.current;
      if (!textarea) {
        debounceTimeoutRef.current = null;
        return;
      }
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
      if (commentsContainerRef.current && writeBackContainerRef.current) {
        commentsContainerRef.current.style.height = `${writeBackContainerRef.current.scrollHeight}px`;
      }
      debounceTimeoutRef.current = null;
    }, 100);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const container = writeBackContainerRef.current;
    if (typeof ResizeObserver === "undefined") {
      recompute();
      return undefined;
    }
    if (!container) {
      recompute();
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      recompute();
    });
    ro.observe(container);
    recompute();
    return () => {
      ro.disconnect();
    };
  }, [recompute]);

  return {
    textareaRef,
    writeBackContainerRef,
    commentsContainerRef,
    recompute,
  };
};
